// DebugPanel — a floating debug widget for stage testing. Collapsed it
// is a small pill in the bottom-left corner (clear of the bottom-right
// cooking banner). Expanded it shows the app version and a live feed of
// fetch calls + console errors, with one-click Copy so a tester can
// paste the client-side truth straight into a chat.
//
// Mounted once in Shell. Kept deliberately self-contained.

import { useEffect, useRef, useState } from "react";
import { APP_VERSION } from "../lib/version";
import { getDebugEntries, getDebugSnapshot, type FetchEntry } from "../lib/debugLog";

export function DebugPanel() {
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // While open, re-render every second so the feed stays live.
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => force((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  const entries = getDebugEntries();
  const pendingCount = entries.filter((e) => e.kind === "fetch" && e.pending).length;
  const errorCount = entries.filter(
    (e) => e.kind === "error" || (e.kind === "fetch" && e.ok === false),
  ).length;

  async function copy() {
    try {
      await navigator.clipboard.writeText(getDebugSnapshot());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked — select-all fallback so the user can ⌘C.
      const el = bodyRef.current;
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Open debug panel"
        className="fixed bottom-4 left-4 z-[60] flex items-center gap-2 rounded-sm border px-3 py-1.5 font-mono text-[10px] tracking-[0.16em] uppercase transition-colors"
        style={{
          background: "#2d1c14",
          borderColor: "#8b6f3a",
          color: "#f5ecd9",
          boxShadow: "0 8px 22px -8px rgba(45,28,20,0.5)",
        }}
      >
        <span aria-hidden>⚙</span>
        <span>debug</span>
        <span className="font-mono normal-case tracking-normal" style={{ color: "#d4a94b" }}>
          v{APP_VERSION}
        </span>
        {pendingCount > 0 && (
          <span style={{ color: "#e0a23c" }}>· {pendingCount} pending</span>
        )}
        {errorCount > 0 && (
          <span style={{ color: "#e0654a" }}>· {errorCount} err</span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 left-4 z-[60] flex flex-col rounded-sm border overflow-hidden"
      style={{
        width: 560,
        maxWidth: "calc(100vw - 32px)",
        height: 420,
        maxHeight: "calc(100vh - 120px)",
        background: "#211710",
        borderColor: "#8b6f3a",
        boxShadow: "0 20px 48px -12px rgba(0,0,0,0.6)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-3 px-3 py-2 border-b shrink-0"
        style={{ borderColor: "#5a4a30", background: "#2d1c14" }}
      >
        <div className="flex items-baseline gap-3 font-mono text-[10px] tracking-[0.16em] uppercase" style={{ color: "#f5ecd9" }}>
          <span aria-hidden>⚙</span>
          <span>Debug</span>
          <span style={{ color: "#d4a94b" }}>v{APP_VERSION}</span>
          <span style={{ color: "#9a8568" }} className="normal-case tracking-normal">
            {entries.length} entries · {pendingCount} pending · {errorCount} err
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={copy}
            className="rounded-sm border px-2.5 py-1 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors"
            style={{ borderColor: "#8b6f3a", color: copied ? "#7ec07e" : "#d4a94b", background: "transparent" }}
          >
            {copied ? "✓ copied" : "⧉ copy all"}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="font-mono text-[14px] leading-none px-1.5"
            style={{ color: "#9a8568" }}
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Feed */}
      <div
        ref={bodyRef}
        className="flex-1 overflow-y-auto px-3 py-2 font-mono text-[10.5px] leading-[1.55]"
        style={{ color: "#cdbfa6" }}
      >
        {entries.length === 0 ? (
          <div style={{ color: "#7a6a52" }}>No activity captured yet.</div>
        ) : (
          entries.map((e, i) => {
            const ts = new Date(e.t).toISOString().slice(11, 19);
            if (e.kind === "fetch") {
              const f = e as FetchEntry;
              const color = f.pending ? "#e0a23c"
                : f.error || f.ok === false ? "#e0654a"
                : "#8fb98f";
              const status = f.pending ? "PENDING"
                : f.error ? `ERR`
                : String(f.status);
              return (
                <div key={i} className="whitespace-pre-wrap break-all">
                  <span style={{ color: "#7a6a52" }}>{ts} </span>
                  <span style={{ color: "#cdbfa6" }}>{f.method} </span>
                  <span style={{ color }}>{status}</span>
                  <span style={{ color: "#7a6a52" }}>{f.ms != null ? ` ${f.ms}ms` : ""} </span>
                  <span style={{ color: "#a99a80" }}>{f.url}</span>
                  {f.error ? <span style={{ color: "#e0654a" }}> — {f.error}</span> : null}
                </div>
              );
            }
            const color = e.kind === "error" ? "#e0654a" : "#e0a23c";
            return (
              <div key={i} className="whitespace-pre-wrap break-all">
                <span style={{ color: "#7a6a52" }}>{ts} </span>
                <span style={{ color }}>{e.kind.toUpperCase()} </span>
                <span style={{ color: "#cdbfa6" }}>{e.text}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
